import type { CSSProperties, ReactNode } from "react";
import type { ColorId } from "@/model/resolve";
import type { SyntaxKey, TokenId } from "@/model/tokens";
import { v } from "./vars";

export function syntaxStyle(key: SyntaxKey): CSSProperties {
  return {
    color: v(`syntax.${key}`),
    fontWeight: `var(--s-${key}-w)` as CSSProperties["fontWeight"],
    fontStyle: `var(--s-${key}-i)`,
    textDecoration: `var(--s-${key}-u)`,
  };
}

/** A syntax-highlighted span. */
export function S({ k, children }: { k: SyntaxKey; children: ReactNode }) {
  return (
    <span data-token={`syntax.${k}`} style={syntaxStyle(k)}>
      {children}
    </span>
  );
}

/** A span colored by any token (foreground and/or background). */
export function C({
  fg,
  bg,
  children,
  className,
  style,
  token,
  bold,
}: {
  fg?: ColorId;
  bg?: ColorId;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  token?: TokenId | ColorId;
  bold?: boolean;
}) {
  return (
    <span
      data-token={token ?? fg ?? bg}
      className={className}
      style={{
        color: fg ? v(fg) : undefined,
        background: bg ? v(bg) : undefined,
        fontWeight: bold ? 700 : undefined,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** Terminal ANSI color by index (0–15). */
export function A({
  i,
  children,
  bold,
  bg,
}: {
  i: number;
  children: ReactNode;
  bold?: boolean;
  bg?: number;
}) {
  return (
    <span
      data-token={`terminal.ansi${i}`}
      style={{
        color: v(`terminal.ansi${i}` as TokenId),
        background: bg === undefined ? undefined : v(`terminal.ansi${bg}` as TokenId),
        fontWeight: bold ? 700 : undefined,
      }}
    >
      {children}
    </span>
  );
}
